import { useState } from "react";
import { PageHeader } from "../components/layout/PageHeader";
import {
  AGENT_CONNECTIONS, AGENT_PROMPT_CARDS, MCP_URL, INSTALL_PROMPT, STATE_LABEL, STATE_TONE,
} from "../lib/sample-data";
import ApiDashboard from "../components/integrations/ApiDashboard";
import {
  Bot, Copy, Check, ExternalLink, Plug, Sparkles, Terminal, Webhook,
} from "lucide-react";
import { cn } from "../lib/utils";

const CONNECTOR_ICON = { agent: Bot, mcp: Plug, cli: Terminal, webhook: Webhook };

export default function AgentAI() {
  const [copied, setCopied] = useState(null);
  const copy = (key, val) => {
    navigator.clipboard?.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(null), 1400);
  };

  return (
    <div data-testid="agent-ai-page" className="space-y-8">
      <PageHeader
        kicker="Agent AI · Skills"
        title="Connect PremierLab to your AI agents"
        description="Use Claude, Cursor, Codex, OpenClaw, Hermes, NemoClaw, and more to generate cinematic images, videos, books, slides, ads, and campaigns directly from your conversations."
        actions={
          <a
            href="#"
            data-testid="agent-docs-btn"
            className="inline-flex items-center gap-2 px-4 h-11 rounded-xl border border-[var(--pl-border)] text-sm text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
          >
            <ExternalLink className="h-4 w-4" /> View Docs
          </a>
        }
      />

      {/* Higgsfield-style connect card */}
      <section
        data-testid="connect-higgsfield-block"
        className="rounded-3xl border border-[var(--pl-border)] p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(39,230,209,0.10), rgba(185,255,47,0.06), rgba(11,47,44,0.6))",
        }}
      >
        <div className="lg:col-span-3 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">
            <Sparkles className="h-3.5 w-3.5 inline -mt-0.5 mr-1.5" />
            Connect Higgsfield to Your AI Agent
          </div>
          <p className="text-sm text-[var(--pl-text)]/85 max-w-xl">
            Connect Higgsfield to Claude, OpenClaw, Hermes, NemoClaw, Cursor, and other AI agents so you can generate cinematic images and videos directly from your conversations.
          </p>
          <ol className="text-sm text-[var(--pl-text-muted)] space-y-1.5 list-decimal pl-5">
            <li>Open your AI agent settings</li>
            <li>Add a custom connector</li>
            <li>Paste your PremierLab or Higgsfield MCP URL</li>
            <li>Connect and sign in</li>
            <li>Ask your agent to generate an image, video, ad, influencer, or campaign</li>
          </ol>
          <div className="flex flex-wrap gap-2 pt-2">
            <CopyBtn
              testId="copy-mcp-url"
              copiedKey="mcp"
              copied={copied}
              onClick={() => copy("mcp", MCP_URL)}
              label="Copy MCP URL"
            />
            <CopyBtn
              testId="copy-install-prompt"
              copiedKey="install"
              copied={copied}
              onClick={() => copy("install", INSTALL_PROMPT)}
              label="Copy Install Prompt"
            />
            <button
              data-testid="connect-agent-btn"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl pl-btn-aqua text-xs"
            >
              <Plug className="h-3.5 w-3.5" /> Connect Agent
            </button>
            <a
              href="#"
              data-testid="agent-view-docs-btn"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View Docs
            </a>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-3">
          <CodeBlock label="MCP URL" value={MCP_URL} testId="mcp-url-block" />
          <CodeBlock label="Install Prompt" value={INSTALL_PROMPT} testId="install-prompt-block" />
        </div>
      </section>

      {/* Agent connections grid */}
      <section className="space-y-3" data-testid="agent-connections">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Agent Connections</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {AGENT_CONNECTIONS.map((a) => {
            const tone = STATE_TONE[a.state];
            const I = CONNECTOR_ICON[a.connector] || Bot;
            return (
              <div
                key={a.id}
                data-testid={`agent-card-${a.id}`}
                className={cn(
                  "pl-contour-card pl-lift rounded-2xl p-5 border bg-[var(--pl-surface)]/60 backdrop-blur-md",
                  tone.border
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-xl bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 grid place-items-center">
                    <I className="h-5 w-5 text-[var(--pl-aqua)]" />
                  </div>
                  <span className={cn("inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border", tone.border, tone.text)}>
                    <span className={cn("h-1.5 w-1.5 rounded-full", tone.dot)} />
                    {STATE_LABEL[a.state]}
                  </span>
                </div>
                <div className="mt-3">
                  <div className="font-medium text-[var(--pl-text)]">{a.name}</div>
                  <div className="text-xs text-[var(--pl-text-muted)] mt-1.5">{a.desc}</div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    data-testid={`agent-connect-${a.id}`}
                    className={cn(
                      "h-9 px-3 rounded-xl text-xs",
                      a.state === "connected"
                        ? "border border-[var(--pl-border)] text-[var(--pl-text)]"
                        : "pl-btn-aqua"
                    )}
                  >
                    {a.state === "connected" ? "Manage" : "Connect"}
                  </button>
                  <button
                    data-testid={`agent-prompt-${a.id}`}
                    onClick={() => copy(`prompt-${a.id}`, INSTALL_PROMPT)}
                    className="h-9 px-3 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] inline-flex items-center gap-1.5"
                  >
                    {copied === `prompt-${a.id}` ? <Check className="h-3.5 w-3.5 text-[var(--pl-aqua)]" /> : <Copy className="h-3.5 w-3.5" />}
                    Setup prompt
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Example prompt cards */}
      <section className="space-y-3" data-testid="agent-prompts">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Try these prompts</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {AGENT_PROMPT_CARDS.map((p) => (
            <div
              key={p.id}
              data-testid={`prompt-card-${p.id}`}
              className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 pl-lift"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-lime)]">{p.title}</div>
              <p className="text-sm text-[var(--pl-text)] mt-2">{p.body}</p>
              <button
                data-testid={`prompt-copy-${p.id}`}
                onClick={() => copy(p.id, p.body)}
                className="mt-4 inline-flex items-center gap-1.5 h-8 px-3 rounded-xl border border-[var(--pl-border)] text-[11px] text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"
              >
                {copied === p.id ? <Check className="h-3.5 w-3.5 text-[var(--pl-aqua)]" /> : <Copy className="h-3.5 w-3.5" />}
                {copied === p.id ? "Copied" : "Copy prompt"}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Embedded API dashboard */}
      <section className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">API Dashboard</div>
        <ApiDashboard scope="agent" />
      </section>
    </div>
  );
}

function CopyBtn({ testId, copiedKey, copied, onClick, label }) {
  const isCopied = copied === copiedKey;
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 px-4 h-10 rounded-xl text-xs transition",
        isCopied
          ? "bg-[var(--pl-lime)] text-[#061414]"
          : "border border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
      )}
    >
      {isCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {isCopied ? "Copied" : label}
    </button>
  );
}

function CodeBlock({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-[var(--pl-border)] bg-black/30 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] mb-1.5">{label}</div>
      <code className="block font-mono text-xs text-[var(--pl-text)] break-all leading-relaxed">{value}</code>
    </div>
  );
}
